import { Injectable, OnDestroy, inject } from "@angular/core";
import _ from "lodash";
import { Subject, merge } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { VirkeskopAffarsformer, VirkeskopKontraktstatus } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard, VirkeskopsatgardKompatibilitet } from "../../models/atgard-models";
import { Geometri } from "../../models/karta-models";
import { VirkeskopKontraktstyp } from "../../models/kontrakt-models";
import { Planeringsstatus } from "../../models/planeringsstatus";
import { AtgardMediatorService } from "../atgard-mediator.service";
import { KontraktMediatorService } from "../kontrakt-mediator.service";
import { VkKartaMediatorService } from "../vk-karta-mediator.service";
import { VirkeskopValidationService } from "./virkeskop-validation.service";

@Injectable()
export class AtgardClientValidationService implements OnDestroy {
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kartaMediatorService = inject(VkKartaMediatorService);

    private readonly destroySrc = new Subject<void>();
    private atgard: Atgard;

    ngOnDestroy(): void {
        this.destroySrc.next();
        this.destroySrc.complete();
    }

    initSubscriptions() {
        const updateAll = () => {
            this.updateRequiredFields();
            this.updateKontraktDependencies();
            this.updateKartaDependencies();
        };

        this.atgardMediatorService.atgard$.pipe(
            takeUntil(this.destroySrc)
        ).subscribe(x => {
            this.atgard = x;
            updateAll();
        });
        this.atgardMediatorService.atgardUpdate$.pipe(
            takeUntil(this.destroySrc)
        ).subscribe(() => updateAll());

        merge(
            this.kartaMediatorService.atgardGeometriChanged$,
            this.kartaMediatorService.atgardGeometriDeleted$,
            this.kartaMediatorService.atgardGeometrier$,
            this.kartaMediatorService.atgardDrivningspunktChanged$,
            this.kartaMediatorService.kontraktGeometriChanged$,
            this.kartaMediatorService.kontraktGeometriDeleted$,
            this.kontraktMediatorService.statusChanged$
        ).pipe(takeUntil(this.destroySrc))
            .subscribe(() => this.updateKartaDependencies());

        merge(
            this.kontraktMediatorService.kontraktGiltigtChanged$,
            this.kontraktMediatorService.statusChanged$
        ).pipe(
            takeUntil(this.destroySrc)
        ).subscribe(() => this.updateKontraktDependencies());

        this.kontraktMediatorService.kontraktstypChanged$.pipe(
            takeUntil(this.destroySrc)
        ).subscribe(() => this.updateRequiredFields());
    }

    private get atgardGeometrier() {
        return this.kartaMediatorService.atgardGeometrier$.value;
    }

    private get kontraktGeometrier() {
        return this.kartaMediatorService.kontraktGeometrier$.value;
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private get atgardGuid() {
        return this.atgardMediatorService.atgard.guid;
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "atgard",
            entityGuids: [
                this.kontrakt.guid,
                this.atgardGuid
            ],
            message: "",
            messageId: "",
            ...message
        };
    }

    private updateKartaDependencies() {
        if (ObjectUtils.isNullOrUndefined(this.atgard)) {
            return;
        }

        const geometri = this.atgardGeometrier?.find(x => x.guid === this.atgard.guid);
        const haskartgeometri = ObjectUtils.hasValue(geometri?.kartgeometri?.geometri);

        this.validationService.updateClientSideValidation(this.atgard.isFardigPlanerat && !haskartgeometri, this.createValidationMessage({
            message: "Rita åtgärdens åtgärdsyta i kartan för att kunna ge åtgärden status tillgänglig.",
            messageId: "atgardStatusKartgeometri"
        }));

        const kartgeometriValidationApplicable = this.atgard.status === Planeringsstatus.Tillgangligt;

        const isApplicableNonU3 = this.kontraktGeometrier && kartgeometriValidationApplicable && !this.kontrakt.isLeveransvirke;

        const isApplicableU3 = this.atgardGeometrier &&
            this.kontrakt.isLeveransvirke &&
            kartgeometriValidationApplicable;

        if (!this.kontraktGeometrier) {
            return;
        }

        const avlagg = this.kontraktGeometrier.filter(x => x.isAvlagg);
        const avlaggIsInvalid = isApplicableNonU3 && (avlagg.some(x => !x.kartgeometri?.tillganglighetsklass) || avlagg.length === 0);

        this.validationService.updateClientSideValidation(avlaggIsInvalid, this.createValidationMessage({
            message: avlagg.length > 0 ? "Kontrakt har avlägg som saknar tillgänglighetsklass." :
                "Kontrakt saknar avlägg, minst 1 krävs och alla kräver tillgänglighetsklass.",
            messageId: "avlaggTillganglighetsklass",
            severity: RuleSeverityLevel.error
        }));

        const larmpunkter = this.kontraktGeometrier.filter(x => x.isLarmpunkt);
        const larmpunkterIsValid = larmpunkter.length >= 1;
        this.validationService.updateClientSideValidation(isApplicableNonU3 && !larmpunkterIsValid, this.createValidationMessage({
            message: "Kontrakt saknar larmkoordinat, minst 1 krävs.",
            messageId: "atgardLarmpunkt",
            severity: RuleSeverityLevel.error
        }));

        const kojplatserValid = this.kontraktGeometrier.some(p => p.isKojplats);
        this.validationService.updateClientSideValidation(!kojplatserValid && isApplicableNonU3, this.createValidationMessage({
            message: "Kontrakt saknar kojplats. Minst 1 krävs.",
            messageId: "atgardKojplats",
            severity: RuleSeverityLevel.error
        }));

        const currentAtgardGeometri = this.atgardGeometrier.find(x => x.atgardGuid === this.atgard.guid);

        const hasAtgardGeometri = ObjectUtils.hasValue(currentAtgardGeometri?.kartgeometri?.geometri);
        const u3Larmpunkter = currentAtgardGeometri?.atgardDrivningspunkter?.filter(x => this.isU3Larmpunkt(x));

        this.validationService.updateClientSideValidation(
            isApplicableU3 &&
            u3Larmpunkter?.length !== 1 &&
            !this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri),
            this.createValidationMessage({
                message: "Åtgärd måste ha 1 larmpunkt.",
                messageId: "atgardLarmpunkt",
                severity: RuleSeverityLevel.error
            }));

        this.validationService.updateClientSideValidation(
            this.kontrakt.isLeveransvirke &&
            u3Larmpunkter?.length > 1 &&
            (this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri) || this.atgard.status !== Planeringsstatus.Tillgangligt),
            this.createValidationMessage({
                message: "Åtgärd får endast ha 1 larmpunkt.",
                messageId: "atgardLarmpunkt",
                severity: RuleSeverityLevel.error
            }));

        this.validationService.updateClientSideValidation(
            isApplicableU3 &&
            !haskartgeometri &&
            !this.atgard.hasKompatibilitet(VirkeskopsatgardKompatibilitet.LeveransvirkeInnanGeometri),
            this.createValidationMessage({
                message: "Rita åtgärdens åtgärdsyta i kartan för att kunna ge åtgärden status tillgänglig.",
                messageId: "atgardStatusKartgeometri",
                severity: RuleSeverityLevel.error
            }));

        this.validationService.updateClientSideValidation(!this.kontrakt.isLeveransvirke && !hasAtgardGeometri, this.createValidationMessage({
            message: "Produktionsområde har inte kunnat fastställas och måttslagskonverteringen blir då inkorrekt. " +
                "Rita åtgärdsgeometrier i kartan så att produktionsområdet kan räknas ut.",
            messageId: "atgardProduktionsomrade",
            severity: RuleSeverityLevel.warning
        }));
    }

    private isU3Larmpunkt(x: Geometri): unknown {
        return x.kartgeometri.subtyp1 === Subtyp1.drivningspunkt &&
            (x.kartgeometri.subtyp2 === Subtyp2.ovrigOverfartLarmpunkt || x.kartgeometri.subtyp2 === Subtyp2.avlaggLarmpunkt);
    }

    private updateKontraktDependencies() {
        if (ObjectUtils.isNullOrUndefined(this.atgard)) {
            return;
        }

        const {
            periodFrom: foreslagetProduktionsdatumFrom,
            periodTom: foreslagetProduktionsdatumTom
        } = this.atgard.foreslagetProduktionsdatum;

        const {
            periodFrom: giltigtFrom,
            periodTom: giltigtTom
        } = this.kontrakt.giltighet;

        const resetDate = (date: Date) => {
            if (!date) {
                return;
            }
            date.setHours(0, 0, 0, 0); // date picker lägger på 1 timmes delay
        };

        const dates = [
            foreslagetProduktionsdatumFrom,
            foreslagetProduktionsdatumTom,
            giltigtFrom,
            giltigtTom
        ];
        dates.forEach(resetDate);

        const validateDateInterval = (date: Date, startDate: Date, endDate: Date) =>
            !_.isNil(date)
            && (_.isNil(startDate) || date >= startDate)
            && (_.isNil(endDate) || date <= endDate);

        const prodApplicable = this.kontrakt.status === VirkeskopKontraktstatus.Kontrakt
            && this.kontrakt.affarsform === VirkeskopAffarsformer.Leveransvirke;

        this.validationService.updateClientSideValidation(
            prodApplicable
            && !validateDateInterval(foreslagetProduktionsdatumFrom, giltigtFrom, foreslagetProduktionsdatumTom),
            this.createValidationMessage({
                messageId: "foreslagetProduktionsdatumFrom",
                message: "Första produktionsdatum får inte vara före kontraktets giltighet eller efter sista produktionsdatum.",
                severity: RuleSeverityLevel.error
            })
        );

        this.validationService.updateClientSideValidation(
            prodApplicable
            && !validateDateInterval(foreslagetProduktionsdatumTom, foreslagetProduktionsdatumFrom, giltigtTom),
            this.createValidationMessage({
                messageId: "foreslagetProduktionsdatumTom",
                message: "Sista produktionsdatum får inte vara före första produktionsdatum eller efter kontraktets giltighet",
                severity: RuleSeverityLevel.error
            })
        );

        this.validationService.updateClientSideValidation(
            this.kontrakt.status === VirkeskopKontraktstatus.Anbud
            && this.kontrakt.affarsform !== VirkeskopAffarsformer.Leveransvirke
            && this.atgard.status === Planeringsstatus.Tillgangligt,
            this.createValidationMessage({
                messageId: "atgardStatus",
                message: "Åtgärdens planeringsstatus kan inte vara tillgänglig om kontraktstatus är anbud.",
                severity: RuleSeverityLevel.error
            })
        );
    }

    private updateRequiredFields() {
        if (ObjectUtils.isNullOrUndefined(this.atgard)) {
            return;
        }

        const atgard = this.atgard;
        const kontrakt = this.kontrakt;

        const applicable = atgard.isFardigPlanerat
            && kontrakt.affarsform !== VirkeskopAffarsformer.Leveransvirke;
        this.validationService.updateClientSideValidation(
            applicable && _.isNil(atgard.nettoareal),
            this.createValidationMessage({
                message: "Ange nettoareal",
                messageId: "atgardNettoareal"
            })
        );

        this.validationService.updateClientSideValidation(
            applicable
            && kontrakt.kontraktTyp === VirkeskopKontraktstyp.Rundvirke
            && !atgard.medelstam,
            this.createValidationMessage({
                message: "Ange medelstam",
                messageId: "atgardMedelstam"
            })
        );

        this.validationService.updateClientSideValidation(
            applicable && !atgard.tillganglighetSkog,
            this.createValidationMessage({
                message: "Ange tillgänglighet skog.",
                messageId: "atgardTillganglighetSkog"
            })
        );

        this.validationService.updateClientSideValidation(
            applicable && !atgard.tillganglighetVag,
            this.createValidationMessage({
                message: "Ange tillgänglighet väg.",
                messageId: "atgardTillganglighetVag"
            })
        );

        this.validationService.updateClientSideValidation(
            applicable && _.isNil(atgard.skotningsavstand),
            this.createValidationMessage({
                message: "Ange skotningsavstånd.",
                messageId: "atgardSkotningsavstand"
            })
        );
    }
}
